import type { HTMLAttributes, ReactNode } from 'react';

// ── Card ────────────────────────────────────────────────────────────────────
export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  selected?: boolean;
}

export function Card({ selected, className = '', children, ...rest }: CardProps) {
  return (
    <div className={`cg-card ${selected ? 'cg-card--selected' : ''} ${className}`} {...rest}>
      {children}
    </div>
  );
}

// ── Badge / status pill ─────────────────────────────────────────────────────
export type BadgeTone = 'success' | 'info' | 'warning' | 'danger' | 'neutral';

export function Badge({ tone = 'neutral', children }: { tone?: BadgeTone; children: ReactNode }) {
  return <span className={`cg-badge cg-badge--${tone}`}>{children}</span>;
}

// ── Tabs ────────────────────────────────────────────────────────────────────
export interface TabItem {
  key: string;
  label: ReactNode;
}

export function Tabs({
  items,
  active,
  onChange,
}: {
  items: TabItem[];
  active: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="cg-tabs" role="tablist">
      {items.map((t) => (
        <button
          key={t.key}
          role="tab"
          aria-selected={active === t.key}
          className={`cg-tab ${active === t.key ? 'cg-tab--active' : ''}`}
          onClick={() => onChange(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Avatar ──────────────────────────────────────────────────────────────────
export function Avatar({ name, src, size = 36 }: { name: string; src?: string; size?: number }) {
  const initial = name.trim().charAt(0).toUpperCase() || '?';
  return (
    <span className="cg-avatar" style={{ width: size, height: size, fontSize: size * 0.42 }}>
      {src ? <img src={src} alt={name} /> : initial}
    </span>
  );
}

// ── StatCard ────────────────────────────────────────────────────────────────
export function StatCard({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div className="cg-statcard">
      <div className="cg-statcard__label">{label}</div>
      <div className="cg-statcard__value">{value}</div>
    </div>
  );
}
